<template>
  <div class="mobile-login-container">
    <div class="mobile-header">
      <div class="logo-container">
        <img src="../../public/img/study.png" width="120px" height="120px" class="logo" />
      </div>
      <h1 class="welcome-title">注册账号</h1>
      <p class="welcome-subtitle">让学习更简单，让转学更轻松</p>
    </div>

    <div class="mobile-form-container">
      <form class="login-form" @submit.prevent="handleRegister">
        <!-- 用户名 -->
        <div class="form-group">
          <label class="form-label" for="username">
            <i class="form-label-icon cubeic-person"></i>
            用户名
          </label>
          <div class="input-wrapper">
            <input
                id="username"
                v-model="localModel.username"
                type="text"
                class="form-input"
                :class="{ 'error': errors.username, 'focused': focusedField === 'username' }"
                placeholder="请输入用户名"
                @focus="focusedField = 'username'"
                @blur="handleBlur('username')"
                @input="clearError('username')"
            />
            <div v-if="errors.username" class="error-message">
              <i class="error-icon cubeic-alert"></i>
              {{ errors.username }}
            </div>
          </div>
        </div>

        <!-- 手机号 -->
        <div class="form-group">
          <label class="form-label" for="phone">
            <i class="form-label-icon cubeic-mobile-phone"></i>
            手机号
          </label>
          <div class="input-wrapper">
            <input
                id="phone"
                v-model="localModel.phone"
                type="tel"
                class="form-input"
                :class="{ 'error': errors.phone, 'focused': focusedField === 'phone' }"
                placeholder="请填写真实手机号，用于重置密码"
                @focus="focusedField = 'phone'"
                @blur="handleBlur('phone')"
                @input="clearError('phone')"
            />
            <div v-if="errors.phone" class="error-message">
              <i class="error-icon cubeic-alert"></i>
              {{ errors.phone }}
            </div>
          </div>
        </div>

        <!-- 密码 -->
        <div class="form-group">
          <label class="form-label" for="password">
            <i class="form-label-icon cubeic-lock"></i>
            密码
          </label>
          <div class="input-wrapper">
            <input
                id="password"
                v-model="localModel.password"
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                :class="{ 'error': errors.password, 'focused': focusedField === 'password' }"
                placeholder="请输入密码"
                @focus="focusedField = 'password'"
                @blur="handleBlur('password')"
                @input="clearError('password')"
            />
            <button
                type="button"
                class="password-toggle"
                @click="togglePassword"
                tabindex="-1"
            >
              <i :class="showPassword ? 'cubeic-eye-visible' : 'cubeic-eye-invisible'"></i>
            </button>
            <div v-if="errors.password" class="error-message">
              <i class="error-icon cubeic-alert"></i>
              {{ errors.password }}
            </div>
          </div>
        </div>

        <!-- 确认密码 -->
        <div class="form-group">
          <label class="form-label" for="checkPassword">
            <i class="form-label-icon cubeic-lock"></i>
            确认密码
          </label>
          <div class="input-wrapper">
            <input
                id="checkPassword"
                v-model="localModel.checkPassword"
                :type="showCheckPassword ? 'text' : 'password'"
                class="form-input"
                :class="{ 'error': errors.checkPassword, 'focused': focusedField === 'checkPassword' }"
                placeholder="请再次输入密码"
                @focus="focusedField = 'checkPassword'"
                @blur="handleBlur('checkPassword')"
                @input="clearError('checkPassword')"
            />
            <button
                type="button"
                class="password-toggle"
                @click="toggleCheckPassword"
                tabindex="-1"
            >
              <i :class="showCheckPassword ? 'cubeic-eye-visible' : 'cubeic-eye-invisible'"></i>
            </button>
            <div v-if="errors.checkPassword" class="error-message">
              <i class="error-icon cubeic-alert"></i>
              {{ errors.checkPassword }}
            </div>
          </div>
        </div>

        <!-- 注册按钮 -->
        <button type="submit" class="mobile-login-btn" :disabled="isSubmitting">
          <span v-if="!isSubmitting">注 册</span>
          <span v-else class="loading-text">
            <i class="cubeic-loading"></i>
            注册中...
          </span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { isUserExisted } from "@/api/login";
import { isPhoneExisted } from "@/api/register";

export default {
  name: 'RegisteredMobile',
  props: {
    model: {
      type: Object,
      required: true
    },
    schema: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localModel: {
        username: this.model.username || '',
        phone: this.model.phone || '',
        password: this.model.password || '',
        checkPassword: this.model.checkPassword || ''
      },
      showPassword: true,
      showCheckPassword: true,
      focusedField: '',
      errors: {
        username: '',
        phone: '',
        password: '',
        checkPassword: ''
      },
      validationState: {
        username: false,
        phone: false,
        password: false,
        checkPassword: false
      },
      isSubmitting: false
    };
  },
  watch: {
    model: {
      deep: true,
      handler(newVal) {
        this.localModel.username = newVal.username || '';
        this.localModel.phone = newVal.phone || '';
        this.localModel.password = newVal.password || '';
        this.localModel.checkPassword = newVal.checkPassword || '';
      },
      immediate: true
    }
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    toggleCheckPassword() {
      this.showCheckPassword = !this.showCheckPassword;
    },
    clearError(field) {
      if (this.errors[field]) {
        this.errors[field] = '';
      }
    },
    handleBlur(field) {
      this.focusedField = '';
      if (field === 'username') {
        this.validateUsername();
      } else if (field === 'phone') {
        this.validatePhone();
      } else if (field === 'password') {
        this.validatePassword();
      } else if (field === 'checkPassword') {
        this.validateCheckPassword();
      }
    },
    async validateUsername() {
      const username = this.localModel.username.trim();
      if (!username) {
        this.errors.username = '用户名为必填项';
        this.validationState.username = false;
        return false;
      }
      if (username.length < 3 || username.length > 11) {
        this.errors.username = '用户名长度为3-11个字符';
        this.validationState.username = false;
        return false;
      }
      try {
        const res = await isUserExisted(username);
        const { message } = res.data;
        if (message) {
          this.errors.username = '用户名已存在';
          this.validationState.username = false;
          return false;
        }
      } catch (err) {
        this.errors.username = '验证失败，请稍后重试';
        this.validationState.username = false;
        return false;
      }
      this.errors.username = '';
      this.validationState.username = true;
      this.syncToParent();
      return true;
    },
    async validatePhone() {
      const phone = this.localModel.phone.trim();
      if (!phone) {
        this.errors.phone = '手机号为必填项';
        this.validationState.phone = false;
        return false;
      }
      if (phone.length !== 11) {
        this.errors.phone = '手机号必须为11位';
        this.validationState.phone = false;
        return false;
      }
      try {
        const res = await isPhoneExisted(phone);
        const { message } = res.data;
        if (message) {
          this.errors.phone = '手机号已存在';
          this.validationState.phone = false;
          return false;
        }
      } catch (err) {
        this.errors.phone = '验证失败，请稍后重试';
        this.validationState.phone = false;
        return false;
      }
      this.errors.phone = '';
      this.validationState.phone = true;
      this.syncToParent();
      return true;
    },
    validatePassword() {
      const password = this.localModel.password.trim();
      if (!password) {
        this.errors.password = '密码为必填项';
        this.validationState.password = false;
        return false;
      }
      if (password.length < 6 || password.length > 16) {
        this.errors.password = '密码长度为6-16个字符';
        this.validationState.password = false;
        return false;
      }
      this.errors.password = '';
      this.validationState.password = true;
      this.syncToParent();
      return true;
    },
    validateCheckPassword() {
      const checkPwd = this.localModel.checkPassword.trim();
      if (!checkPwd) {
        this.errors.checkPassword = '请再次输入密码';
        this.validationState.checkPassword = false;
        return false;
      }
      if (checkPwd !== this.localModel.password) {
        this.errors.checkPassword = '两次密码输入不一致';
        this.validationState.checkPassword = false;
        return false;
      }
      this.errors.checkPassword = '';
      this.validationState.checkPassword = true;
      this.syncToParent();
      return true;
    },
    syncToParent() {
      this.$emit('input', { ...this.localModel });
    },
    async validate() {
      const usernameValid = await this.validateUsername();
      const phoneValid = await this.validatePhone();
      const passwordValid = this.validatePassword();
      const checkPwdValid = this.validateCheckPassword();
      return usernameValid && phoneValid && passwordValid && checkPwdValid;
    },
    async handleRegister(e) {
      e.preventDefault();
      if (this.isSubmitting) return;

      this.syncToParent();
      const isValid = await this.validate();

      if (isValid) {
        this.isSubmitting = true;
        this.$emit('register');
        setTimeout(() => {
          this.isSubmitting = false;
        }, 3000);
      } else {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style>
@import "../assets/stytels/login-mobile.css";
</style>
